// MobileEditorView.jsx
//
// ⚠️ IMPORTANT: Is file me koi NAYI business logic nahi hai (except ki
// "addPages" jo parent se prop ke through aata hai, khud yahan nahi likha).
// Sab data/functions parent (PhotobookEditor.jsx) se props ke through aate hain.
//
// UI details:
// 1. ScaledCanvas wrapper — page ko fixed "design size" (jaisa desktop pe
//    hota hai) me render karke poore card ko CSS transform:scale se chhota
//    karta hai. Text/frames sab ek hi ratio me proportionally chhote hote
//    hain. (aspect-ratio ki jagah padding-top % trick — purane
//    browsers/webviews me bhi reliably kaam karta hai.)
// 2. Layout alternating hai: Page 1 akela (chhota fixed width) → 2 pages
//    side-by-side → 1 akela → 2 side-by-side ... Cover apni alag
//    full-width row me, sabse upar.
// 3. List ke end me ek dashed "Add Page" box — click pe 3 pages ek sath
//    (same layout repeat) add hote hain (parent ka naya `addPages` prop).
// 4. Jab kisi (khaali) frame wale page ka selectedFrame set ho, us card ke
//    niche ek chhota upload-icon dikhta hai — tap karne pe photo-picker
//    khulta hai (fileRef.current.click(), jo already existing handleFile
//    logic use karta hai). Bahar click karne pe icon apne aap gayab ho
//    jata hai (parent me pehle se bana outside-click useEffect isse
//    handle karta hai, bas is icon ko bhi 'selection UI' me tag kiya hai).

import { useRef, useState, useEffect } from "react";

const CARD_WIDTH = 150; // 👈 Page cards ka fixed width (px)

function ScaledCanvas({ baseWidth, baseHeight, children }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.3);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setScale(w / baseWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [baseWidth]);

  return (
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden"
      style={{ paddingTop: `${(baseHeight / baseWidth) * 100}%` }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: baseWidth,
          height: baseHeight,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function MobileEditorView({
  // data
  pages,
  cur,
  selectedFrame,
  editingCoverText,
  productData,

  // setters
  setCur,
  setSelectedTextId,
  setEditingCoverText,
  setPages,

  // existing handlers
  handlePhotoClick,
  handleCaptionClick,
  handleFrameDrop,
  updateFrameProp,
  handleCoverTextChange,
  addPages,

  // existing page-render components
  PageCanvas,
  CoverSpread,
  CollageCanvas,
  renderPolaroidPage,

  // upload
  fileRef,

  bottomOffset = 190,
}) {
  const renderPageBody = (pg, idx) => {
    if (pg.isCollage) {
      return (
        <CollageCanvas
          page={pg}
          selectedFrame={cur === idx ? selectedFrame : null}
          editingText={cur === idx ? editingCoverText : null}
          onPhotoClick={(fid) => { setCur(idx); handlePhotoClick(fid, idx); }}
          onFrameDrop={(fid, file, url) => handleFrameDrop(fid, file, idx, url)}
          onFrameUpdate={(fid, props) => updateFrameProp(fid, props, idx)}
          onTextDblClick={(tid) => { setCur(idx); setSelectedTextId(tid); setEditingCoverText(tid); }}
          onTextChange={(tid, val) =>
            setPages((prev) => prev.map((p, i) => (i !== idx ? p : { ...p, texts: p.texts.map((t) => (t.id === tid ? { ...t, content: val,isDefault: false } : t)) })))
          }
          onTextBlur={() => setEditingCoverText(null)}
        />
      );
    }
    return (
      renderPolaroidPage(pg, {
        selectedFrame: cur === idx ? selectedFrame : null,
        editingCoverText: cur === idx ? editingCoverText : null,
        onPhotoClick: (fid) => { setCur(idx); handlePhotoClick(fid, idx); },
        onFrameDrop: (fid, file, url) => handleFrameDrop(fid, file, idx, url),
        onFrameUpdate: (fid, props) => updateFrameProp(fid, props, idx),
        onTextDblClickMulti: (tid) => { setCur(idx); setSelectedTextId(tid); setEditingCoverText(tid); },
        onTextChangeMulti: (tid, val) =>
          setPages((prev) => prev.map((p, i) => (i !== idx ? p : { ...p, texts: p.texts.map((t) => (t.id === tid ? { ...t, content: val, isDefault: false } : t)) }))),
        onTextBlur: () => setEditingCoverText(null),
      }) || (
        <PageCanvas
          page={pg}
          selectedFrame={cur === idx ? selectedFrame : null}
          onPhotoClick={(fid) => { setCur(idx); handlePhotoClick(fid, idx); }}
          onCaptionClick={(fid) => { setCur(idx); handleCaptionClick(fid, idx); }}
          onFrameDrop={(fid, file, _, url) => handleFrameDrop(fid, file, idx, url)}
          onFrameUpdate={(fid, props) => updateFrameProp(fid, props, idx)}
          editingText={cur === idx ? editingCoverText : null}
          onTextDblClick={(tid) => { setCur(idx); setSelectedTextId(tid); setEditingCoverText(tid); }}
          onTextChange={(tid, val) =>
            setPages((prev) => prev.map((p, i) => (i !== idx ? p : { ...p, texts: p.texts.map((t) => (t.id === tid ? { ...t, content: val, isDefault: false } : t)) })))
          }
          onTextBlur={() => setEditingCoverText(null)}
        />
      )
    );
  };

const pageCard = (pgItem, pIdx) => {
  const isActive = cur === pIdx;
  const isSelectedHere = isActive && selectedFrame !== null;

  return (
    <div key={pgItem.id} className="flex flex-col items-center" style={{ width: CARD_WIDTH }}>
      <div className="relative w-full rounded-sm overflow-hidden bg-white" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}>
        {isActive ? (
          // Sirf active page: poora interactive canvas (drag/zoom/select sab kaam karega)
          <ScaledCanvas baseWidth={460} baseHeight={400}>
            {renderPageBody(pgItem, pIdx)}
          </ScaledCanvas>
        ) : (
          // Baaki pages: halka static preview — koi ResizeObserver/touch listener nahi
          <div
            className="relative w-full cursor-pointer"
            style={{ aspectRatio: "460 / 400", background: pgItem.bg }}
            onClick={() => setCur(pIdx)}
          >
            {pgItem.frames.map((f, fi) => (
              <div
                key={fi}
                className="absolute"
                style={{
                  left: `${f.x}%`, top: `${f.y}%`, width: `${f.w}%`, height: `${f.h}%`,
                  background: f.image ? `url(${f.image}) center/cover` : f.color,
                  borderRadius: 1,
                }}
              />
            ))}
                       {(pgItem.texts || []).map((t) => (
              <div
                key={t.id}
                className="absolute whitespace-pre-line"
                style={{
                  left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%`,
                  fontFamily: t.font,
                  fontSize: `${parseFloat(t.size) * (CARD_WIDTH / 460)}px`,
                  color: t.color,
                  textAlign: t.align || "left",
                  pointerEvents: "none",
                  lineHeight: 1.2,
                }}
              >
                {t.content}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="text-center text-[11px] font-medium text-gray-600 mt-1.5">Page {pIdx}</div>

      {isSelectedHere && (
        <button
          data-selection-pill
          onClick={(e) => { e.stopPropagation(); fileRef.current.click(); }}
          className="mt-1 w-8 h-8 rounded-full bg-gray-900 text-white shadow-lg flex items-center justify-center"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
        </button>
      )}
    </div>
  );
};

  const rows = [];
  {
    let i = 0;
    let singleNext = true; // Page 1 akela, phir alternate (1, 2, 1, 2 ...)

    while (i < pages.length) {
      const pg = pages[i];

      // Cover hamesha apni alag full-width row, sabse upar
      if (pg.isCover) {
        rows.push(
          <div key={pg.id} className="flex flex-col items-center">
            <div className="w-full rounded-sm overflow-hidden bg-white" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}>
              <ScaledCanvas baseWidth={960} baseHeight={400}>
                <CoverSpread
                  page={pg}
                  selectedFrame={cur === 0 ? selectedFrame : null}
                  editingCoverText={cur === 0 ? editingCoverText : null}
                  onPhotoClick={(fid) => { setCur(0); handlePhotoClick(fid, 0); }}
                  onCaptionClick={(fid) => { setCur(0); handleCaptionClick(fid, 0); }}
                  onFrameDrop={(fid, file, url) => handleFrameDrop(fid, file, 0, url)}
                  onFrameUpdate={(fid, props) => updateFrameProp(fid, props, 0)}
                  onCoverTextDblClick={(tid) => { setCur(0); setSelectedTextId(tid); setEditingCoverText(tid); }}
                  onCoverTextChange={handleCoverTextChange}
                  onCoverTextBlur={() => setEditingCoverText(null)}
                />
              </ScaledCanvas>
            </div>
            <div className="flex justify-between w-full text-[11px] text-gray-500 mt-1.5 px-1">
              <span>Back Cover</span>
              <span className="font-medium text-gray-700">Front Cover</span>
            </div>
          </div>
        );
        i++;
        continue;
      }

      if (singleNext) {
        rows.push(<div key={`row-${i}`} className="flex justify-center">{pageCard(pg, i)}</div>);
        i += 1;
      } else {
        const pg2 = pages[i + 1];
        rows.push(
          <div key={`row-${i}`} className="flex gap-4 justify-center">
            {pageCard(pg, i)}
            {pg2 && pageCard(pg2, i + 1)}
          </div>
        );
        i += pg2 ? 2 : 1;
      }
      singleNext = !singleNext;
    }
  }

  return (
    <>
      {/* ── Pages: alternating 1 / 2 / 1 / 2 layout, top-se-bottom scroll ── */}
      <div
        data-lenis-prevent
        className="md:hidden flex-1 min-h-0 overflow-y-auto px-5 pt-5"
        style={{ paddingBottom: bottomOffset, WebkitOverflowScrolling: "touch" }}
      >
        <div className="flex flex-col gap-7">
          {rows}

          {/* ── Add Page: dashed box, click pe 3 pages ek sath add ── */}
          <div className="flex justify-center">
            <button
              onClick={() => addPages && addPages(3)}
              className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-400 rounded-md text-gray-500 hover:border-gray-600 hover:text-gray-700 transition-colors"
              style={{ width: CARD_WIDTH, aspectRatio: "460 / 400" }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-7 h-7">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6" />
                <line x1="9" y1="14" x2="15" y2="14" />
                <line x1="12" y1="11" x2="12" y2="17" />
              </svg>
              <span className="text-[12px] font-medium">Add Page</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Upload images: hamesha bottom pe fixed ── */}
      <div data-mobile-tool-panel className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white rounded-t-2xl shadow-[0_-6px_24px_rgba(0,0,0,0.10)] px-5 pt-5 pb-6 border-t border-gray-100">
        <p className="text-center text-lg font-semibold text-gray-800 mb-1">
          {productData?.name || "Photobook"}
        </p>
        <p className="text-center text-[13px] text-gray-400 mb-4 leading-snug">
          Select images from available sources and begin editing your project.
        </p>
        <button
          onClick={() => fileRef.current.click()}
          className="w-full py-3 bg-black text-white text-sm font-semibold rounded-full flex items-center justify-center gap-2"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Upload images
        </button>
      </div>
    </>
  );
}